import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { LineChart, Line, YAxis, XAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { FlaskConical, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { money, signedMoney, pct, pnlColor } from "../lib/format";

const DEFAULT_SYMBOLS = ["ES", "NQ", "MES", "MNQ", "MBT", "MET", "XRP"];

export const BacktestModal = ({ open, onOpenChange, symbols }) => {
  const SYMBOLS = symbols && symbols.length ? symbols : DEFAULT_SYMBOLS;
  const [symbol, setSymbol] = useState(SYMBOLS[0]);
  const [bars, setBars] = useState(2000);
  const [threshold, setThreshold] = useState(6);
  const [running, setRunning] = useState(false);
  const [res, setRes] = useState(null);

  const runIt = async () => {
    setRunning(true);
    try {
      const r = await api.backtest({ symbol, bars: Number(bars), threshold: Number(threshold) });
      setRes(r);
    } finally {
      setRunning(false);
    }
  };

  const Metric = ({ label, value, cls }) => (
    <div className="px-3 py-2 border-r border-b border-zinc-800">
      <div className="text-[9px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`font-mono text-sm font-semibold ${cls || "text-zinc-100"}`}>{value}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm font-semibold tracking-tight">
            <FlaskConical size={16} className="text-blue-400" /> Strategy Backtester
            <span className="text-[10px] text-zinc-500 font-normal">· simulated historical engine</span>
          </DialogTitle>
          <DialogDescription className="text-[11px] text-zinc-500">
            Replays the SMC confluence strategy over generated historical bars and reports win rate, profit factor, drawdown and the equity curve.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="text-[9px] uppercase text-zinc-500">Symbol</span>
            <div className="flex gap-1">
              {SYMBOLS.map((s) => (
                <button
                  key={s}
                  data-testid={`bt-symbol-${s}`}
                  onClick={() => setSymbol(s)}
                  className={`px-2.5 py-1 text-[11px] font-mono font-semibold rounded-sm border transition-colors duration-150
                    ${symbol === s ? "bg-blue-500/15 text-blue-400 border-blue-500/40" : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[9px] uppercase text-zinc-500">Bars ({bars})</span>
            <input
              data-testid="bt-bars"
              type="range" min="500" max="4000" step="250" value={bars}
              onChange={(e) => setBars(e.target.value)}
              className="w-32 accent-blue-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[9px] uppercase text-zinc-500">Threshold ({threshold})</span>
            <input
              data-testid="bt-threshold"
              type="range" min="2" max="11" step="1" value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-32 accent-blue-500"
            />
          </div>
          <button
            data-testid="run-backtest-btn"
            onClick={runIt}
            disabled={running}
            className="ml-auto flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-sm bg-blue-600 text-white hover:bg-blue-500 transition-colors duration-150 disabled:opacity-50"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />}
            {running ? "Running…" : "Run Backtest"}
          </button>
        </div>

        {res && !res.error && (
          <div className="mt-1" data-testid="backtest-results">
            <div className="grid grid-cols-4 border-t border-l border-zinc-800">
              <Metric label="Net P&L" value={signedMoney(res.net_pnl)} cls={pnlColor(res.net_pnl)} />
              <Metric label="Return" value={pct(res.return_pct)} cls={pnlColor(res.return_pct)} />
              <Metric label="Win Rate" value={`${res.win_rate}%`} />
              <Metric label="Profit Factor" value={res.profit_factor} cls={res.profit_factor >= 1 ? "text-green-500" : "text-red-500"} />
              <Metric label="Trades" value={`${res.total_trades} (${res.wins}W/${res.losses}L)`} />
              <Metric label="Avg Win" value={money(res.avg_win)} cls="text-green-500" />
              <Metric label="Avg Loss" value={money(res.avg_loss)} cls="text-red-500" />
              <Metric label="Max Drawdown" value={pct(-res.max_drawdown)} cls="text-red-400" />
            </div>

            <div className="h-52 mt-3 border border-zinc-800 bg-[#09090b] p-2">
              <div className="text-[9px] uppercase tracking-wide text-zinc-500 mb-1">Equity Curve</div>
              <ResponsiveContainer width="100%" height="90%" minWidth={1} minHeight={40}>
                <LineChart data={res.equity_curve} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                  <XAxis dataKey="i" hide />
                  <YAxis domain={["dataMin", "dataMax"]} width={54} tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  <Tooltip
                    contentStyle={{ background: "#09090b", border: "1px solid #27272a", fontSize: 11, fontFamily: "JetBrains Mono" }}
                    labelStyle={{ color: "#a1a1aa" }}
                    formatter={(v) => [money(v), "equity"]}
                  />
                  <ReferenceLine y={res.starting_balance} stroke="#3f3f46" strokeDasharray="3 3" />
                  <Line type="monotone" dataKey="equity" stroke={res.net_pnl >= 0 ? "#22c55e" : "#ef4444"} strokeWidth={1.6} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {res?.error && <div className="text-red-400 text-xs font-mono">{res.error}</div>}
      </DialogContent>
    </Dialog>
  );
};
